import React, { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircularProgress, Container, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { ErrorResponse, ReportConfigDto } from '@thingslog/repositories';
import { useToast } from '@thingslog/ui-components';
import Header from '../../components/header';
import TitleHeader from '../../components/TitleHeader/TitleHeader';
import { scheduledReportsQueryClient } from '../../clients/ReactQueryClients/ReactQueryClients';
import ScheduledReportConfigsTable from './ScheduledReportConfigsTable';
import useDetermineCompanyId from '../../hooks/useDetermineCompanyId';

const ScheduledReportConfigs: FC = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Get company ID using hook
  const selectedCompanyId = useDetermineCompanyId();

  const { useAllReportConfigsForCompany } = useMemo(() => scheduledReportsQueryClient, []);

  const { data, isLoading, refetch } = useAllReportConfigsForCompany(selectedCompanyId || 0, {
    enabled: selectedCompanyId !== null,
    onError: (error: AxiosError<ErrorResponse>) => {
      const errorMessage = error.response?.data.message || t('report_configs_default_error');

      toast({
        type: 'error',
        message: errorMessage,
        duration: 5000,
      });
    },
  });

  if (selectedCompanyId === null) {
    return (
      <Header>
        <Container>
          <div className="text-2xl m-auto mt-20 text-center">
            {t('report_configs_select_company_message')}
          </div>
        </Container>
      </Header>
    );
  }

  return (
    <Header>
      <Container>
        <section className="flex justify-center mb-6">
          <TitleHeader title={t('report_configs_header')} />
        </section>
        <section className="flex justify-end mb-4">
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={(): void => navigate('/app/ScheduledReportsConfig/create')}
          >
            {t('report_configs_create_button')}
          </Button>
        </section>
        <section>
          {isLoading ? (
            <div className="flex justify-center p-8">
              <CircularProgress size={80} />
            </div>
          ) : (
            <ScheduledReportConfigsTable
              reportConfigRows={
                data
                  ? data.map((config: ReportConfigDto, index: number) => ({
                      ...config,
                      rowId: index,
                    }))
                  : []
              }
              onRefetch={refetch}
            />
          )}
        </section>
      </Container>
    </Header>
  );
};

export default ScheduledReportConfigs;
